// src/utils/emailSanitizer.js
//
// inboxxss-7c4f2a91 — S3, security audit 2026-10-02 finding #3.
//
// THE ONE PLACE incoming email HTML is made safe before the CRM inbox renders it. Before this,
// EmailContentDisplay put the raw body into dangerouslySetInnerHTML: the Microsoft path did no
// cleaning at all, and the Gmail path ran a three-line regex that `<img src=x onerror=…>` (no
// quotes) walked straight past. Any outsider who emailed a connected staff member could run
// script as that staff member on app.spatchy.co.
//
// WHY DOMPurify AND NOT A SANDBOXED IFRAME: the email keeps rendering inline in the CRM layout,
// the way it does today. An iframe needs height-measuring code, gives every email its own
// scrollbar and fights the app's styles. DOMPurify is the standard, actively maintained
// sanitizer, parses with the browser's own HTML parser, and is pinned to a release past every
// published advisory (>= 3.4.16).
//
// WHAT SURVIVES (so it still looks like an email): text formatting, tables, inline `style`
// attributes, remote images (http/https/cid/data images), and normal links — which are forced
// to open in a new tab with rel="noopener noreferrer".
// WHAT DOES NOT: scripts, every on* event handler, javascript:/vbscript:/data: links, forms and
// every form control, iframes/frames/objects/embeds, <base>, <meta>, <link>, SVG and MathML,
// and <style> blocks. <style> is dropped on purpose: a <style> block is GLOBAL, so an email
// could restyle (or hide, or overlay) the whole CRM page. Real-world email is written with
// inline styles because mail clients strip <style> too, so the email still looks right.
// Inline style VALUES are also screened for the old script-in-CSS tricks (expression(),
// javascript: inside url(), behavior:, -moz-binding).
//
// The container that displays the result additionally sets `contain: content` so a
// position:fixed inline style can't escape the message box and cover the CRM.

import createDOMPurify from 'dompurify';

const FORBID_TAGS = [
    'script', 'style', 'link', 'meta', 'base', 'title',
    'form', 'input', 'button', 'textarea', 'select', 'option', 'optgroup', 'fieldset', 'datalist', 'output',
    'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'portal',
    'svg', 'math', 'template', 'noscript', 'dialog',
];

const FORBID_ATTR = ['action', 'formaction', 'srcdoc', 'ping', 'xmlns', 'autofocus'];

// Script hidden in CSS values. Remote background images (url(https://…)) stay.
const DANGEROUS_CSS = /expression\s*\(|javascript\s*:|vbscript\s*:|behavior\s*:|-moz-binding|url\s*\(\s*['"]?\s*data\s*:\s*text/i;

let purifier = null;

// A DEDICATED DOMPurify instance, so these hooks never leak into any other use of DOMPurify.
// Built lazily: it needs a window (the browser, or jsdom under jest).
function getPurifier() {
    if (purifier) return purifier;
    if (typeof window === 'undefined') return null;
    purifier = createDOMPurify(window);

    purifier.addHook('uponSanitizeAttribute', (_node, data) => {
        if (data.attrName === 'style' && DANGEROUS_CSS.test(data.attrValue || '')) {
            data.keepAttr = false;
        }
    });

    purifier.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A' && node.hasAttribute('href')) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });

    return purifier;
}

/**
 * Make an incoming email's HTML safe to render in the CRM.
 * Fails CLOSED: with no DOM to sanitize against, it returns '' rather than the raw HTML.
 * @param {string} html
 * @returns {string}
 */
export function sanitizeEmailHtml(html) {
    if (typeof html !== 'string' || !html) return '';
    const p = getPurifier();
    if (!p) return '';
    return p.sanitize(html, {
        USE_PROFILES: { html: true },
        FORBID_TAGS,
        FORBID_ATTR,
        ALLOW_DATA_ATTR: false,
        ALLOW_UNKNOWN_PROTOCOLS: false,
    });
}
